/**
 * Transcode Job Types
 * Shared types for transcode job monitoring between client and server
 */

// === Base Types ===

export interface TranscodeJob {
  id: string;
  status: string | null;
  progress: number | null;
  duration: number | null;
  filesize: number | null;
  message: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  assetID: string | null;
  clientID: string | null;
  eventID: string | null;
  jobType: string | null;
  destBucket: string | null;
  destKey: string | null;
  srcBucket: string | null;
  srcKey: string | null;
  thumbnails: string | null;
  playlist: string | null;
}

// === Stall Detection ===

export type StallStatus = 'normal' | 'stalled' | 'silent-failure';

export interface TrackedJob extends TranscodeJob {
  /** Last known progress percentage */
  lastProgressValue: number;
  /** Timestamp (ms) when progress last increased */
  lastProgressChangeAt: number;
  /** Current stall status based on thresholds */
  stallStatus: StallStatus;
}

// === API Types ===

export interface TranscodeJobFilters {
  clientID?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  page?: number;
}

export interface TranscodeJobsMeta {
  startDate: string;
  endDate: string;
  count: number;
  limit: number;
  /** Current page (only for paginated requests) */
  page?: number;
  /** Total number of matching records (only for paginated requests) */
  totalCount?: number;
  /** Total number of pages (only for paginated requests) */
  totalPages?: number;
}

export interface TranscodeJobsResponse {
  jobs: TranscodeJob[];
  meta: TranscodeJobsMeta;
}

// === WSS Message Types ===

/**
 * Transcode WSS action types from Scaledrone
 * - start: Job begins (has full asset metadata)
 * - processing: Progress update (0-100)
 * - complete: Job finished successfully
 * - failed/error: Job failed (structure TBD)
 */
export type TranscodeActionType = 'start' | 'processing' | 'complete' | 'failed' | 'error';

export interface TranscodeWSSMessage {
  type: 'transcode_job';
  jobID: string;
  clientID: string;
  progress: number;
  status: string;
  timestamp: number;
  raw: unknown;
  assetID?: string;
  eventID?: string;
}

// === Configuration ===

export const TRANSCODE_CONFIG = {
  /** Maximum date range for transcode job queries (days) */
  MAX_DATE_RANGE_DAYS: 90,
  /** Interval for re-evaluating stall status on client (ms) */
  STALL_CHECK_INTERVAL_MS: 30_000,
} as const;

// === Store Config ===

export interface StallDetectionConfig {
  /** Time without progress before job is considered stalled (default: 5 min) */
  stallThresholdMs: number;
  /** Time at 100% without completion before silent failure (default: 10 min) */
  silentFailureThresholdMs: number;
}

export const DEFAULT_STALL_CONFIG: StallDetectionConfig = {
  stallThresholdMs: 5 * 60 * 1000, // 5 minutes
  silentFailureThresholdMs: 10 * 60 * 1000, // 10 minutes
};

// === Utility Functions ===

export function detectStallStatus(
  job: TrackedJob,
  now: number,
  config: StallDetectionConfig
): StallStatus {
  if (job.status !== 'processing') {
    return 'normal';
  }

  const timeSinceChange = now - job.lastProgressChangeAt;

  // Silent failure: stuck at 100% waiting for final status
  if (job.progress === 100) {
    return timeSinceChange > config.silentFailureThresholdMs ? 'silent-failure' : 'normal';
  }

  // Progress stall: no progress increase for threshold period
  return timeSinceChange > config.stallThresholdMs ? 'stalled' : 'normal';
}

export function createTrackedJob(job: TranscodeJob, now: number): TrackedJob {
  return {
    ...job,
    lastProgressValue: job.progress ?? 0,
    lastProgressChangeAt: now,
    stallStatus: 'normal',
  };
}
